import { ClipboardCopy } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";

import { textoSdr, tituloSdr } from "../dominio/sdr";
import type { Negocio, Nota } from "../dominio/tipos";
import type { Sale } from "@/components/atomic-crm/types";

/**
 * «Abrir SDR» (Jan y Robert, 13/10/2026): en «Consultoría agendada», copia al portapapeles el
 * texto para abrir el chat «SDR - [Contacto] - [Negocio]» (dominio/sdr.ts). Solo lee.
 */
export const BotonAbrirSdr = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [copiando, setCopiando] = useState(false);

  const copiar = async () => {
    setCopiando(true);
    try {
      const { data: notas } = await dataProvider.getList<Nota>("notas", {
        filter: { negocio_id: negocio.negocio_id },
        sort: { field: "fecha", order: "ASC" },
        pagination: { page: 1, perPage: 500 },
      });
      // Los nombres se piden aquí: al abrir la ficha la lista de usuarios puede no haber llegado.
      const { data: usuarios } = await dataProvider.getList<Sale>("sales", {
        filter: {},
        sort: { field: "id", order: "ASC" },
        pagination: { page: 1, perPage: 200 },
      });
      const nombre = (id?: unknown) => {
        const u = usuarios.find((x) => String(x.id) === String(id));
        return u ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() : "";
      };
      const texto = textoSdr({
        negocio,
        notas,
        nombreUsuario: nombre,
        t: translate,
      });
      await navigator.clipboard.writeText(texto);
      notify("nevora.sdr.copiado", {
        type: "success",
        messageArgs: { titulo: tituloSdr(negocio, translate) },
      });
    } catch {
      notify("nevora.sdr.error_copiar", { type: "error" });
    } finally {
      setCopiando(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="h-11 md:h-9"
      title={translate("nevora.sdr.ayuda")}
      disabled={copiando}
      onClick={copiar}
      data-testid="abrir-sdr"
    >
      <ClipboardCopy className="size-4 mr-1.5" />
      {translate("nevora.sdr.boton")}
    </Button>
  );
};
